import React from 'react';
import { MdEdit } from 'react-icons/md';
import { IoIosWarning } from 'react-icons/io';
import { IoPlay } from 'react-icons/io5';
import { useNodeTheme } from './NodeTheme';
import { describeTrigger, isGenericTitle } from './nodeSummary';

// Colores de las variables, iguales a los chips del editor (VariableChips):
// las que vienen de un nodo (API, WebHook) con su color; las generales en gris.
const VARIABLE_SOURCE_COLORS = {
  queryApi: '#7ECCD8',
  webHook: '#FC6530',
};
const GENERAL_VARIABLE_COLOR = '#A7ADA7';

// Pinta las {{variables}} del contenido como chips compactos, sin las llaves.
const renderContent = (text) =>
  String(text)
    .split(/(\{\{[^}]+\}\})/g)
    .filter(Boolean)
    .map((part, index) => {
      if (!(part.startsWith('{{') && part.endsWith('}}'))) {
        return <React.Fragment key={index}>{part}</React.Fragment>;
      }
      const full = part.slice(2, -2).trim();
      const sourceType = full.includes('.') ? full.match(/-(\w+)\./)?.[1] : null;
      const name = full.includes('.') ? full.split('.').slice(1).join('.') : full;
      return (
        <span
          key={index}
          title={full}
          style={{
            display: 'inline-block',
            padding: '0 5px',
            margin: '1px 1px',
            borderRadius: '4px',
            backgroundColor: VARIABLE_SOURCE_COLORS[sourceType] || GENERAL_VARIABLE_COLOR,
            color: '#fff',
            fontSize: '10.5px',
            fontWeight: 500,
            lineHeight: '16px',
            verticalAlign: '1px',
          }}
        >
          {name}
        </span>
      );
    });

// Chrome compartido para la tarjeta de nodo en el canvas:
//   regla superior de color (identifica el tipo de un vistazo)
//   + tile con el color tintado al 15% y el icono en color pleno
//   + eyebrow (nombre del tipo) / título / subtítulo
//   + insignia flotante de "editar" al seleccionar y aviso de error.
// El color se usa como tinte, no como bloque saturado.
// Cada tipo aporta su icono/color/typeName, sus Handles (prop `handles`, la
// cantidad y posición varía por tipo) y contenido extra opcional (`children`,
// p.ej. las filas de rama con handles propios).
//
// Título: si el usuario no le puso uno propio, se muestra el contenido del nodo
// (`summary`: el mensaje, la regla, la petición...) para distinguirlo sin
// abrirlo. Subtítulo: el trigger con el que se llega al nodo.
const NodeCardShell = ({
  icon: Icon,
  color,
  typeName,
  title: rawTitle,
  summary,
  trigger,
  subtitle: rawSubtitle,
  error,
  selected,
  first,
  handles,
  children,
}) => {
  const theme = useNodeTheme();

  // El título por defecto ("Respuesta Básica", "Condiciones"...) repite lo que ya
  // dice el eyebrow, así que solo se muestra si el usuario lo personalizó.
  const customTitle = !isGenericTitle(rawTitle);
  const triggerText = first ? null : rawSubtitle ?? describeTrigger(trigger);
  const content = summary || (customTitle ? '' : rawTitle);

  // El nodo inicial lleva un anillo permanente (visible incluso con zoom out) y
  // una insignia "INICIO": es la fuente de la que nace todo el flujo.
  const boxShadow = selected
    ? `0 8px 32px ${color}66, 0 0 0 3px ${color}4D`
    : first
    ? `0 6px 22px ${color}59, 0 0 0 2px ${color}CC`
    : '0 4px 14px rgba(0, 0, 0, 0.18)';

  return (
    <div style={{ position: 'relative' }}>
      {first && (
        <div
          style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            borderRadius: '999px',
            backgroundColor: color,
            color: '#fff',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.7px',
            textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            whiteSpace: 'nowrap',
            zIndex: 1001,
            pointerEvents: 'none',
          }}
        >
          <IoPlay style={{ fontSize: '9px' }} />
          Inicio
        </div>
      )}
      {selected && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            left: '-10px',
            backgroundColor: color,
            color: '#fff',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
          }}
        >
          <MdEdit style={{ fontSize: '14px' }} />
        </div>
      )}
      {handles}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '210px',
          borderRadius: '14px',
          overflow: 'hidden',
          backgroundColor: theme.cardBg,
          border: `1px solid ${first && !selected ? color : `color-mix(in srgb, ${color} 35%, ${theme.border})`}`,
          boxShadow,
          transform: selected ? 'scale(1.03)' : 'scale(1)',
          transition: 'all 0.2s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '11px',
            padding: '13px 14px',
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              minWidth: '32px',
              borderRadius: '9px',
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon style={{ fontSize: '16px', color: '#FFFFFF' }} />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              flex: 1,
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: 600,
                  letterSpacing: '0.7px',
                  textTransform: 'uppercase',
                  color,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {typeName}
              </span>
              {error && (
                <IoIosWarning
                  style={{ fontSize: '14px', color: '#FFCC00', flexShrink: 0 }}
                />
              )}
            </div>
            {customTitle && (
              <span
                title={rawTitle}
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {rawTitle}
              </span>
            )}
            {/* Primero por dónde se llega al nodo, luego qué hace */}
            {triggerText && (
              <span
                title={`Se activa con: ${triggerText}`}
                style={{
                  fontSize: '11px',
                  color: theme.textSecondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {triggerText}
              </span>
            )}
            {content && (
              <span
                style={{
                  fontSize: '12px',
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: `color-mix(in srgb, ${theme.textPrimary} 55%, ${theme.textSecondary})`,
                  marginTop: '2px',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }}
              >
                {renderContent(content)}
              </span>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default NodeCardShell;
